import { Component, inject, Input, OnInit } from '@angular/core';
import { NgIf, NgFor } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, catchError } from 'rxjs/operators';
import { Folder, FolderPlanogram } from 'src/app/models/folder';
import { StoreSearchEntry } from 'src/app/models/store-search-entry';
import { ApiService } from 'src/app/services/api.service';
import { PlanogramApiService } from 'src/app/services/planogram-api.service';
import { ToastService } from 'src/app/services/toasts.service';
import { CreateReferencePlanogramResult } from 'src/app/models/reference-planogram';

/**
 * Creates linked (reference) planograms of a master into one or more stores. The linked planograms
 * always show the master's layout but are stamped with their own store's details, and are view-only.
 */
@Component({
  selector: 'app-create-reference-planogram-modal',
  templateUrl: './create-reference-planogram-modal.component.html',
  imports: [FormsModule, NgbTypeahead, NgIf, NgFor],
})
export class CreateReferencePlanogramModalComponent implements OnInit {
  @Input() planogram!: FolderPlanogram;
  @Input() planogramFolderId!: string;
  @Input() rootFolders: Folder[] = [];

  selectedStores: StoreSearchEntry[] = [];
  storeModel: string | StoreSearchEntry = '';
  folderId: string = '';
  /** When on (default), each linked planogram is seeded with its own store's sales/CPI. When off,
   *  it shows the reference planogram's own sales/CPI and no per-store flow items are stored. */
  useStoreSales = true;
  flatFolders: { id: string; name: string }[] = [];
  isSubmitting = false;

  constructor(
    public activeModal: NgbActiveModal,
    private apiService: ApiService,
    private planogramApiService: PlanogramApiService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.flatFolders = this.flattenFolders(this.rootFolders);
    this.folderId = this.planogramFolderId || '';
  }

  private flattenFolders(folders: Folder[], level = 0): { id: string; name: string }[] {
    let flat: { id: string; name: string }[] = [];
    for (const folder of folders) {
      flat.push({
        id: folder.guid,
        name: (level > 0 ? '  '.repeat(level) + '↳ ' : '') + folder.name,
      });
      if (folder.subFolders) {
        flat = flat.concat(this.flattenFolders(folder.subFolders, level + 1));
      }
    }
    return flat;
  }

  searchStores = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => this.apiService.searchStores(term, this.planogram.dataSourceId).pipe(catchError(() => of([]))))
    );

  storeFormatter = (store: StoreSearchEntry) => store.name;

  onStoreSelect(event: { item: StoreSearchEntry; preventDefault: () => void }): void {
    event.preventDefault();
    const store = event.item;
    this.storeModel = '';
    if (store && !this.selectedStores.some(s => s.storeGuid === store.storeGuid)) {
      this.selectedStores = [...this.selectedStores, store];
    }
  }

  removeStore(store: StoreSearchEntry): void {
    this.selectedStores = this.selectedStores.filter(s => s.storeGuid !== store.storeGuid);
  }

  async submit(): Promise<void> {
    if (this.selectedStores.length === 0 || this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      const result: CreateReferencePlanogramResult = await this.planogramApiService.createReferencePlanogram({
        referencePlanogramId: this.planogram.guid,
        storeIds: this.selectedStores.map(s => s.storeGuid),
        folderId: this.folderId || null,
        useStoreSales: this.useStoreSales,
      });

      const createdCount = result.created.length;
      const failureCount = result.failures.length;
      if (createdCount > 0) {
        this.toastService.show(
          `Created ${createdCount} linked planogram(s)${failureCount ? `, ${failureCount} skipped` : ''}.`,
          { classname: 'bg-success text-light', delay: 4000 }
        );
      } else {
        this.toastService.show(
          `No linked planograms were created${failureCount ? ` (${failureCount} skipped — different data source)` : ''}.`,
          { classname: 'bg-warning text-dark', delay: 5000 }
        );
      }
      this.activeModal.close(result);
    } catch (err) {
      // The shared apiRequest error handler already surfaces a danger toast, so just log here.
      console.error(err);
    } finally {
      this.isSubmitting = false;
    }
  }

  cancel(): void {
    this.activeModal.close(null);
  }
}
